'use client'

import React, { useEffect, useImperativeHandle, useRef } from 'react'
import {
  IconButton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../'
import { DafitiBlack } from '../../brands'
import { Hamburguer, Search } from '../../icons'
import { cn, warnIfEmptyLabel } from '../../utils'
import type { IconButtonProps } from '../'
import type { ComponentProps, PropsWithChildren, ReactNode } from 'react'

const HEADER_SEARCH_STATE_ID = '__closet-ui-header-search-state'

/**
 * Header component that contains the brand, search, and actions.
 * It is a responsive component that adapts to different screen sizes.
 */
export const HeaderRoot = ({
  children,
  className,
  ...props
}: PropsWithChildren<React.ComponentProps<'header'>>) => {
  return (
    <header
      {...props}
      className={cn(
        'relative border-b-[1px] border-gray-200 bg-white lg:border-gray-300',
        className,
      )}
    >
      {/* Header Grid */}
      <div
        className={cn(
          "grid grid-cols-[auto_1fr_auto_auto] grid-rows-[64px_auto] [grid-template-areas:'menu_brand_actions''search_search_search'] md:grid-cols-3 lg:grid-cols-[auto_2fr_auto_auto] lg:grid-rows-1 lg:[grid-template-areas:'brand_search_account_actions']",
          'container min-h-[64px] lg:h-[72px] lg:border-b-0',
        )}
      >
        {children}
      </div>
    </header>
  )
}

type HeaderBrandProps<T extends React.ElementType = 'div'> = {
  ariaLabel: string
  as?: T
  logo?: ReactNode
} & React.ComponentPropsWithoutRef<T>

/**
 * Brand component for the header.
 */
export const HeaderBrand = <T extends React.ElementType>({
  as,
  ariaLabel,
  logo,
  ...props
}: HeaderBrandProps<T>) => {
  warnIfEmptyLabel(ariaLabel, 'HeaderBrand: ariaLabel')

  const Comp = as || 'a'

  return (
    <div className="md:pl-none flex items-center justify-start pl-6 [grid-area:brand] md:justify-center md:pl-0 lg:justify-start">
      <Comp role="link" {...props} aria-label={ariaLabel}>
        {logo || <DafitiBlack className="lg:w-26 w-20" />}
      </Comp>
    </div>
  )
}

/**
 * Menu toggle button for the header.
 */
export const HeaderMenuToggle = ({
  ariaLabel,
  ...props
}: PropsWithChildren<IconButtonProps> & { ariaLabel: string }) => {
  warnIfEmptyLabel(ariaLabel, 'HeaderMenuToggle: ariaLabel')

  return (
    <div className="flex items-center [grid-area:menu] lg:hidden lg:w-auto">
      <IconButton size="md" variant="subtle" {...props} aria-label={ariaLabel}>
        <Hamburguer aria-hidden="true" />
      </IconButton>
    </div>
  )
}

/**
 * Search component for the header.
 */
export const HeaderSearch = ({
  ref,
  children,
  onOpenSearch,
  onCloseSearch,
  onInitialOpen,
  initiallyOpen = false,
  open,
  onBlur,
}: Omit<React.ComponentProps<'div'>, 'ref' | 'open'> & {
  onOpenSearch?: () => void
  onCloseSearch?: () => void
  ref?: React.Ref<HeaderSearch | null>
  onInitialOpen?: () => void | undefined
  initiallyOpen?: boolean
  open?: boolean
  onBlur?: (e: React.FocusEvent<HTMLDivElement>) => void
}) => {
  const searchContainerClassName = cn(
    'invisible h-0 transition-[height] duration-300 w-full lg:visible lg:h-auto lg:max-w-[776px]',
    'peer-checked:visible peer-checked:h-[80px] peer-checked:lg:h-auto',
  )

  const checkboxRef = useRef<HTMLInputElement>(null)
  const hasOpenedInitially = useRef(false)

  useImperativeHandle(
    ref,
    () => ({
      toggle: () => checkboxRef.current?.click(),
    }),
    [],
  )

  useEffect(() => {
    if (initiallyOpen && !hasOpenedInitially.current) {
      hasOpenedInitially.current = true
      onInitialOpen?.()
    }
  }, [initiallyOpen, onInitialOpen])

  return (
    <div
      className={'flex items-center justify-center [grid-area:search]'}
      onBlur={onBlur}
    >
      {/* Checkbox acting as state handler to allow used to open search before javascript loaded  */}
      <input
        type="checkbox"
        data-testid="header-search-state-cbx"
        aria-hidden="true"
        className="peer invisible hidden"
        id={HEADER_SEARCH_STATE_ID}
        ref={checkboxRef}
        checked={open ?? checkboxRef.current?.checked ?? initiallyOpen}
        onChange={(e) =>
          e.target.checked ? onOpenSearch?.() : onCloseSearch?.()
        }
      />

      {/* Mobile: Search Spacing  */}
      <div
        className={cn(searchContainerClassName, 'invisible lg:hidden')}
        aria-hidden="true"
      />

      {/* Search Container */}
      {/* The overflow-hidden previously set on the div below was responsible for
      fixing the expand animation when clicking the search button on mobile.
      However, it also prevented any outside content (Search Suggestions) from being displayed,
      as overflow-hidden blocked this. Therefore, overflow-hidden was removed for now and 
      a new we should look into this in card NP-1288: https://dafiti.jira.com/browse/NP-1288. */}
      <div
        className={cn(
          'lg:max-w-auto container absolute flex flex-col items-center justify-center border-t-[1px] border-gray-200 lg:relative lg:mx-8 lg:overflow-visible lg:border-t-0 lg:bg-transparent',
          searchContainerClassName,
        )}
        data-testid="search-container"
      >
        {children}
      </div>
    </div>
  )
}

export type HeaderSearch = {
  /**
   * Toggles the search input visibility.
   */
  toggle: () => void
}

/**
 * Search action button for the header.
 */
export const HeaderSearchAction = ({
  label,
  ...props
}: ComponentProps<typeof HeaderAction>) => {
  return (
    <HeaderAction
      label={label}
      {...props}
      asChild
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          document.getElementById(HEADER_SEARCH_STATE_ID)?.click()
        }

        props.onKeyDown?.(e)
      }}
      className="lg:hidden"
    >
      <label htmlFor={HEADER_SEARCH_STATE_ID} tabIndex={0}>
        <Search aria-hidden="true" />
      </label>
    </HeaderAction>
  )
}

export const HeaderAccount = ({
  className,
  ...props
}: React.ComponentProps<'div'>) => {
  return (
    <div
      {...props}
      className={cn(
        'mr-8 hidden items-center whitespace-pre-wrap text-end text-xs [grid-area:account] lg:flex',
        className,
      )}
    />
  )
}

/**
 * Actions container for the header.
 */
export const HeaderActions = ({
  className,
  ...props
}: PropsWithChildren<React.ComponentProps<'div'>>) => (
  <div
    {...props}
    className={cn(
      'flex items-center justify-end gap-2 [grid-area:actions] lg:w-auto lg:gap-1',
      className,
    )}
  />
)

type HeaderActionProps = PropsWithChildren<IconButtonProps> & {
  label: string
  ariaLabel?: string
  badge?: boolean
  badgeProps?: {
    className?: string
    content?: ReactNode
  }
}

/**
 * Action button for the header.
 */
export const HeaderAction = ({
  label,
  ariaLabel,
  badge,
  badgeProps,
  ...props
}: HeaderActionProps) => {
  warnIfEmptyLabel(label, 'HeaderAction: label')

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="relative">
            <IconButton
              asChild
              size="md"
              variant="subtle"
              {...props}
              aria-label={ariaLabel || label}
            />
            {badge ? (
              <div
                data-testid="bag-badge"
                className={cn(
                  'animate-in bg-support-300 text-2xs animation-duration-500 slide-in-from-left-50 slide-in-from-top-50 zoom-in-[2] absolute bottom-0 right-0 flex size-4 items-center justify-center rounded-full font-semibold text-white ease-in-out',
                  badgeProps?.className,
                )}
              >
                {badgeProps?.content}
              </div>
            ) : null}
          </div>
        </TooltipTrigger>
        <TooltipContent className="hidden lg:block">{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
